import { useTranslation } from 'react-i18next'
import type { Scorer, Team } from '@brochure/shared'
import { cn } from '@/lib/utils'

type ScorersListProps = { scorers: Scorer[]; teams: Team[] }

/**
 * Cannonieri sulla stessa carta della classifica: posizione (il primo su fondo sole, il podio su fondo mare),
 * nome con la squadra sotto, barra proporzionale al massimo e bicchieri segnati in grande.
 */
export function ScorersList({ scorers, teams }: ScorersListProps) {
  const { t } = useTranslation()
  if (scorers.length === 0) return <p className="font-body text-sm text-ink-soft">{t('zambePong.scorers.empty')}</p>

  const max = scorers[0].goals || 1
  const teamName = (teamId: string | null) => teams.find((team) => team.id === teamId)?.name

  return (
    <ol className="max-w-3xl divide-y divide-line/70 overflow-hidden rounded-xl border border-ink/10 bg-paper font-body shadow-card">
      {scorers.map((scorer, index) => {
        const isTop = index === 0
        return (
          <li key={scorer.playerId} className={cn('flex items-center gap-3 px-4 py-3', isTop && 'bg-sun/15')}>
            <span
              className={cn(
                'grid size-7 shrink-0 place-items-center rounded-full text-xs font-bold tabular-nums',
                isTop ? 'bg-sun text-ink' : index < 3 ? 'bg-sea/20 text-sea-ink' : 'text-ink-soft',
              )}
            >
              {index + 1}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-ink">{scorer.name}</p>
              {teamName(scorer.teamId) && <p className="truncate text-xs text-ink-soft">{teamName(scorer.teamId)}</p>}
              <div className="mt-1.5 h-1.5 rounded-full bg-ink/10">
                <div className={cn('h-full rounded-full', isTop ? 'bg-sun' : 'bg-sea')} style={{ width: `${(scorer.goals / max) * 100}%` }} />
              </div>
            </div>
            <span className="w-8 shrink-0 text-right font-body text-2xl tabular-nums text-ink">{scorer.goals}</span>
          </li>
        )
      })}
    </ol>
  )
}
